import first from "lodash/first";
import { useRouter } from "next/router";
import type { FunctionComponentWithRef, MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import { useContentItemProviders } from "scmp-app/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "scmp-app/components/content/content-item-render/types";
import { getContentSponsor } from "scmp-app/components/content/content-sponsor-tag/helpers";
import { usePlusArticlePopup } from "scmp-app/components/plus/article-popup/hooks";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { SearchEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import type { searchContentItemContent$key } from "scmp-app/queries/__generated__/searchContentItemContent.graphql";

import {
  Authors,
  Container,
  Cover,
  Footer,
  Header,
  LiveTagContainer,
  PrintHeadline,
  StyledEntityLink,
  StyledImagePlusFlag,
  Summary,
} from "./styles";

export type Props = ContentItemRenderVariantProps & {
  reference: searchContentItemContent$key;
};

export const ContentItemSearch: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  ref: forwardReference,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment searchContentItemContent on Content {
        __typename
        entityId
        authors {
          entityId
        }
        topics {
          entityId
        }
        sectionsV2 {
          value {
            ... on Section {
              entityId
            }
            ... on VideoSection {
              entityId
            }
          }
        }
        ...entityLink
        ...helpersUseContentSponsorContent
        ...hooksContentItemProvidersContent
          @arguments(
            withLiveTag: true
            withHeadline: true
            withCoverImage: true
            withCoverImageSize540x360: true
            withSummary: true
            withActionBar: true
            withPrintHeadline: true
            withSponsorTag: true
            withAuthorImage: true
            withAuthorLink: true
            withVideoDurationOverlay: true
            withGalleryOverlay: true
          )
        ...hooksPlusArticleLinkHandlerArticle
        ...helpersCheckCanShowInPlusPlatformContent
        ...helpersCheckIsPlusArticle
      }
    `,
    reference_,
  );

  const providers = useContentItemProviders(content);
  const { route } = useRouter();

  const isOnPlusSearch = useMemo(() => route === plusSearchPathname, [route]);

  const needToShowPlusFlag = useMemo(() => checkIsPlusArticle(content), [content]);

  const header = useMemo(
    () => (
      <>
        {needToShowPlusFlag && <StyledImagePlusFlag />}
        <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
        {providers.headline({
          hideTooltip: true,
          useSectionTags: true,
        })}
      </>
    ),
    [needToShowPlusFlag, providers],
  );

  const cover = providers.coverImage({
    responsiveVariants: {
      desktopUp: "size540x360",
      mobileUp: "size540x360",
      tabletUp: "size540x360",
    },
  });

  const contentSponsor = getContentSponsor(content);
  const isSponsorArticle = notEmpty(contentSponsor?.name);
  const summary = isSponsorArticle ? null : providers.summary();

  const { currentValue } = useSearchContext();

  const authors = useMemo(
    () => (
      <>
        {providers.authorImage({
          ignoreAuthorType: true,
        })}
        {providers.author({
          ignoreAuthorType: true,
          isMultiple: true,
          onClick: (_entityUuid: string, entityId?: string) => {
            // GA4 21.2.1
            sendGA4Tracking({
              action: "click",
              category: "search",
              customized_parameters: {
                author_id: entityId,
                is_plus_article: needToShowPlusFlag ? "yes" : "no",
                search_text: currentValue,
              },
              subcategory: "result",
            });
          },
        })}
      </>
    ),
    [currentValue, needToShowPlusFlag, providers],
  );

  const listOfSectionIds =
    first(content.sectionsV2)
      ?.value?.map(section => section?.entityId)
      .join(",") ?? "";

  const listOfTopicIds = content.topics?.map(topic => topic?.entityId).join(",") ?? "";

  const { handleOpen } = usePlusArticlePopup();

  const handleItemClick = useCallback(
    (event: MouseEvent) => {
      // GA4 21.2.1
      sendGA4Tracking({
        action: "click",
        category: "search",
        customized_parameters: {
          article_id: content?.entityId,
          author_id: content?.authors?.[0]?.entityId,
          is_plus_article: needToShowPlusFlag ? "yes" : "no",
          search_text: currentValue,
          section_id: listOfSectionIds,
          topic_id: listOfTopicIds,
        },
        subcategory: "result",
      } as SearchEvent);

      if (isOnPlusSearch) {
        event.preventDefault();
        handleOpen(content);
      }
    },
    [
      content,
      currentValue,
      handleOpen,
      isOnPlusSearch,
      listOfSectionIds,
      listOfTopicIds,
      needToShowPlusFlag,
    ],
  );

  const footer = isSponsorArticle
    ? providers.sponsorTag?.({
        color: "#001246",
        gap: "4px",
        layoutDirection: "horizontal",
        nameFontSize: "12px",
        typeFontSize: "12px",
      })
    : providers.actionBar({
        displayDateVariant: "publishedDate",
        isBookmarkVisible: false,
        isCommentsVisible: false,
      });

  const renderContent = useCallback(() => {
    const resultContent = () => {
      switch (content.__typename) {
        case "Gallery":
        case "Video":
          return (
            <>
              <Header>{header}</Header>
              <Cover>
                {cover}
                {content.__typename === "Video" &&
                  providers.video?.durationOverlay({
                    iconSize: "small",
                    variant: "homepage",
                  })}
                {content.__typename === "Gallery" &&
                  providers.gallery?.indicator({ withValue: false })}
              </Cover>
              <Authors />
              <Summary />
              <PrintHeadline />
              <Footer>{footer}</Footer>
            </>
          );
        default:
          return (
            <>
              <Header>{header}</Header>
              <Cover>{cover}</Cover>
              <Authors>{authors}</Authors>
              <Summary>{summary}</Summary>
              <Footer>{footer}</Footer>
              <PrintHeadline>{providers.printHeadline()}</PrintHeadline>
            </>
          );
      }
    };

    return (
      <StyledEntityLink
        onClick={handleItemClick}
        reference={content}
        target={isOnPlusSearch ? "_blank" : "_self"}
      >
        {resultContent()}
      </StyledEntityLink>
    );
  }, [
    authors,
    content,
    cover,
    footer,
    handleItemClick,
    header,
    isOnPlusSearch,
    providers,
    summary,
  ]);

  return (
    <Container className={className} ref={forwardReference}>
      {renderContent()}
    </Container>
  );
};

ContentItemSearch.displayName = "ContentItemSearch";
